// utils/displayError.js
// ============================================
// What an error looks like to the user.
//
// The server already answers with clear, bilingual messages, and a lost
// connection surfaces as a ConnectionError with its own bilingual text — those
// are shown as they are. What must never reach the screen is technical text:
// the browser's "TypeError: Failed to fetch", a hardcoded developer string
// such as "Failed to fetch visibility-scoped employees" or "HTTP 500",
// a JSON parse error, or English text in the Arabic interface. Those are
// replaced by a calm, generic message in the current language.
//
// Used by the shared alert components and the page-level error areas, so
// every page gets the same behaviour without each call site changing.
// ============================================
import { connectionErrorMessage } from "./connectionError";

const FALLBACKS = {
  generic: {
    en: "Something went wrong. Please try again.",
    ar: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
  },
  load: {
    en: "Something went wrong while loading this information. Please try again.",
    ar: "حدث خطأ أثناء تحميل هذه البيانات. يرجى المحاولة مرة أخرى.",
  },
};

// Developer / transport wording that is never meaningful to a user.
const TECHNICAL_PATTERNS = [
  /failed to fetch/i,
  /fetch failed/i,
  /network ?error/i,
  /load failed/i,
  /\bhttp\s*\d{3}\b/i,
  /\bstatus(?: code)?:?\s*\d{3}\b/i,
  /^\s*\d{3}\b/,
  /unexpected (token|end of json)/i,
  /\bjson\b/i,
  /\b(type|syntax|reference|range)error\b/i,
  /cannot read propert/i,
  /is not a function/i,
  /is not defined/i,
  /\bundefined\b/i,
  /\bnull\b/i,
  /\babort(ed|error)?\b/i,
  /the operation was aborted/i,
  /econn(refused|reset)/i,
  /internal server error/i,
  /request failed/i,
  /not found in/i,
  /<\/?[a-z][\s\S]*>/i, // an HTML error page body
];

// Arabic block (U+0600–U+06FF).
const ARABIC_LETTERS = new RegExp("[\\u0600-\\u06FF]");

// Without an explicit language (components that don't receive one), use the
// interface language the LanguageProvider persists.
const pickLanguage = (language) => {
  if (language === "ar" || language === "en") return language;
  try {
    return localStorage.getItem("language") === "ar" ? "ar" : "en";
  } catch (err) {
    return "en";
  }
};

/**
 * True when `message` is not something a user should read.
 * `strictLanguage`: also reject English text in the Arabic interface — used
 * for load-error areas, where the fallback says the same thing. General
 * alerts leave it off so a meaningful (if untranslated) validation message
 * is not swapped for a vaguer one.
 */
export const isTechnicalMessage = (message, language, { strictLanguage = false } = {}) => {
  const text = String(message || "").trim();
  if (!text) return true;
  if (TECHNICAL_PATTERNS.some((pattern) => pattern.test(text))) return true;
  if (strictLanguage && pickLanguage(language) === "ar" && !ARABIC_LETTERS.test(text)) return true;
  return false;
};

/**
 * Turn an error (Error object, server body, or string) into text that is
 * safe and meaningful to show.
 *
 * @param {unknown} error
 * @param {"en"|"ar"} language
 * @param {{ kind?: "generic" | "load", strictLanguage?: boolean }} [options]
 *   kind "load" also turns on strictLanguage unless it is set explicitly.
 */
export const getDisplayError = (error, language, { kind = "generic", strictLanguage = kind === "load" } = {}) => {
  const lang = pickLanguage(language);
  const fallback = (FALLBACKS[kind] || FALLBACKS.generic)[lang];
  if (!error) return fallback;

  if (error?.isConnectionError) return connectionErrorMessage(lang);

  // A server error body: prefer its own bilingual messages.
  const fromBody =
    error?.messages?.[lang] ||
    (typeof error === "object" && !(error instanceof Error) ? error?.error || error?.message : null);

  const candidate =
    fromBody ||
    (typeof error === "string" ? error : error?.message) ||
    "";

  return isTechnicalMessage(candidate, lang, { strictLanguage }) ? fallback : String(candidate).trim();
};
